import { useState, useRef, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Leaf, Drumstick, X, ImagePlus, Search, Check, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getMenu, saveMenuItem, deleteMenuItem, getMenuMeta, saveMenuMeta, errMsg } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { uid } from "@/lib/local";
import { inr } from "@/lib/format";
import { MENU_CATEGORIES, MENU_SUBCATEGORIES, groupByCategory, orderCategories, UNCATEGORIZED } from "@/lib/menuTaxonomy";
import { PageHeader } from "@/components/common";
import { cn } from "@/lib/utils";

const NONE = "__none__";
const ADD = "__add__";
const EMPTY = { name: "", price: "", description: "", is_veg: true, available: true, image: "", variants: [], category: "", subcategory: "" };

export default function Menu() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["menu"], queryFn: async () => (await getMenu()).data });
  const { data: metaData } = useQuery({ queryKey: ["menu-meta"], queryFn: async () => (await getMenuMeta()).data });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [delItem, setDelItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [addCat, setAddCat] = useState(false);
  const [addSub, setAddSub] = useState(false);
  const [dragId, setDragId] = useState(null);
  const fileRef = useRef(null);

  const rawItems = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  // Merge external menu items with our locally-stored category / sub-category / order.
  const items = useMemo(() => {
    const metaMap = {};
    for (const m of Array.isArray(metaData) ? metaData : []) metaMap[m.item_id] = m;
    return rawItems.map((it) => ({
      ...it,
      category: metaMap[it.id]?.category || "",
      subcategory: metaMap[it.id]?.subcategory || "",
      sort: metaMap[it.id]?.sort ?? null,
    }));
  }, [rawItems, metaData]);

  // Search + category-chip filter, then order within each sub-category by saved sort.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = items;
    if (q) {
      list = list.filter((it) => {
        const hay = [it.name, it.description, it.category, it.subcategory, ...(it.variants || []).map((v) => v.name)]
          .filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
      });
    }
    if (catFilter) list = list.filter((it) => (it.category || UNCATEGORIZED) === catFilter);
    return [...list].sort((a, b) => {
      const sa = a.sort == null ? 9999 : a.sort;
      const sb = b.sort == null ? 9999 : b.sort;
      if (sa !== sb) return sa - sb;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [items, query, catFilter]);

  const groups = useMemo(() => groupByCategory(filtered), [filtered]);

  // Category chips = categories actually present (ordered), for tap-to-filter.
  const chipCats = useMemo(() => {
    const set = new Set();
    for (const it of items) set.add(it.category || UNCATEGORIZED);
    return orderCategories([...set]);
  }, [items]);

  const canDrag = !query.trim();

  const reorderMut = useMutation({
    mutationFn: async (rows) => {
      for (const row of rows) await saveMenuMeta(row);
    },
    onError: (e) => toast.error(errMsg(e)),
    onSettled: () => qc.invalidateQueries({ queryKey: ["menu-meta"] }),
  });

  // Reorder an item within its sub-category and persist the new sort order.
  const handleDrop = (subItems, toId) => {
    if (!dragId || dragId === toId) { setDragId(null); return; }
    const ids = subItems.map((i) => i.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(toId);
    if (from < 0 || to < 0) { setDragId(null); return; }
    const newIds = [...ids];
    newIds.splice(to, 0, newIds.splice(from, 1)[0]);
    const rows = newIds.map((id, idx) => {
      const it = subItems.find((x) => x.id === id);
      return { item_id: id, category: it.category, subcategory: it.subcategory, sort: idx };
    });
    // Optimistic cache update for instant reorder.
    qc.setQueryData(["menu-meta"], (old = []) => {
      const map = new Map((old || []).map((m) => [m.item_id, { ...m }]));
      for (const r of rows) map.set(r.item_id, { ...(map.get(r.item_id) || {}), ...r });
      return [...map.values()];
    });
    reorderMut.mutate(rows);
    setDragId(null);
  };

  // Selectable options = defaults + any custom values the vendor already used.
  const catOptions = useMemo(() => {
    const set = new Set(MENU_CATEGORIES);
    for (const it of items) if (it.category) set.add(it.category);
    return [...set];
  }, [items]);
  const subOptions = useMemo(() => {
    const set = new Set(MENU_SUBCATEGORIES);
    for (const it of items) if (it.subcategory) set.add(it.subcategory);
    return [...set];
  }, [items]);

  const openNew = () => { setForm(EMPTY); setAddCat(false); setAddSub(false); setOpen(true); };
  const openEdit = (it) => {
    setForm({
      id: it.id,
      name: it.name || "",
      price: String(it.price ?? ""),
      description: it.description || "",
      is_veg: it.is_veg !== false,
      available: it.available !== false,
      image: it.image || "",
      category: it.category || "",
      subcategory: it.subcategory || "",
      variants: (it.variants || []).map((v) => ({ _k: uid(), name: v.name, price: String(v.price) })),
    });
    setAddCat(false);
    setAddSub(false);
    setOpen(true);
  };

  const saveMut = useMutation({
    mutationFn: async ({ meta, ...body }) => {
      const res = await saveMenuItem(body);
      const saved = res?.data || {};
      const itemId = saved.id || body.id;
      // Persist metadata on edits (so it can be changed/cleared), or on new items
      // only when a category/sub-category was actually chosen.
      const hasMeta = (meta.category || "") !== "" || (meta.subcategory || "") !== "";
      if (itemId && (body.id || hasMeta)) {
        await saveMenuMeta({ item_id: itemId, category: meta.category, subcategory: meta.subcategory });
      }
      return saved;
    },
    onSuccess: () => {
      toast.success("Menu item saved");
      qc.invalidateQueries({ queryKey: ["menu"] });
      qc.invalidateQueries({ queryKey: ["menu-meta"] });
      setOpen(false);
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const delMut = useMutation({
    mutationFn: (id) => deleteMenuItem(id),
    onSuccess: () => { toast.success("Item deleted"); qc.invalidateQueries({ queryKey: ["menu"] }); setDelItem(null); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await compressImage(file);
      setForm((f) => ({ ...f, image: dataUrl }));
    } catch {
      toast.error("Couldn't process that image");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = () => {
    if (!form.name.trim()) return toast.error("Item name is required");
    const variants = (form.variants || [])
      .filter((v) => v.name.trim())
      .map((v) => ({ name: v.name.trim(), price: parseFloat(v.price) || 0 }));
    saveMut.mutate({
      ...(form.id ? { id: form.id } : {}),
      name: form.name.trim(),
      price: parseFloat(form.price) || 0,
      description: form.description || "",
      is_veg: form.is_veg,
      available: form.available,
      image: form.image || null,
      variants: variants.length ? variants : null,
      meta: { category: form.category || "", subcategory: form.subcategory || "" },
    });
  };

  const addVariant = () => setForm((f) => ({ ...f, variants: [...f.variants, { _k: uid(), name: "", price: "" }] }));
  const setVariant = (i, key, val) => setForm((f) => ({ ...f, variants: f.variants.map((v, idx) => (idx === i ? { ...v, [key]: val } : v)) }));
  const rmVariant = (i) => setForm((f) => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }));

  const renderCard = (it, subItems) => (
    <div
      key={it.id}
      draggable={canDrag}
      onDragStart={() => canDrag && setDragId(it.id)}
      onDragOver={(e) => canDrag && e.preventDefault()}
      onDrop={(e) => { if (canDrag) { e.preventDefault(); handleDrop(subItems, it.id); } }}
      className={cn(
        "rounded-md border border-border bg-card p-4 flex items-start justify-between gap-3",
        canDrag && "cursor-grab active:cursor-grabbing",
        dragId === it.id && "opacity-50"
      )}
      data-testid={`menu-item-${it.id}`}
    >
      <div className="flex items-start gap-3 min-w-0">
        {canDrag && (
          <GripVertical className="h-5 w-5 text-muted-foreground/60 shrink-0 mt-4" data-testid={`drag-${it.id}`} />
        )}
        {it.image ? (
          <img src={it.image} alt={it.name} className="h-14 w-14 rounded-md object-cover shrink-0" data-testid={`menu-photo-${it.id}`} />
        ) : (
          <div className="h-14 w-14 rounded-md bg-muted grid place-items-center shrink-0 text-muted-foreground">
            <ImagePlus className="h-5 w-5" />
          </div>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {it.is_veg !== false ? (
              <Leaf className="h-4 w-4 text-emerald-600 shrink-0" />
            ) : (
              <Drumstick className="h-4 w-4 text-red-600 shrink-0" />
            )}
            <span className="font-semibold">{it.name}</span>
            {it.available === false && (
              <span className="text-xs font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded">Out of stock</span>
            )}
            {it.subcategory && (
              <span className="text-[10px] font-semibold tracking-wide text-primary bg-accent px-1.5 py-0.5 rounded uppercase">{it.subcategory}</span>
            )}
          </div>
          {it.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{it.description}</p>}
          {(it.variants || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {it.variants.map((v, i) => (
                <span key={`${v.name}-${i}`} className="text-xs bg-muted rounded px-2 py-0.5">
                  {v.name} · {inr(v.price)}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="font-mono font-semibold">{inr(it.price)}</span>
        <Button size="icon" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-item-${it.id}`}>
          <Pencil className="h-4 w-4" />
        </Button>
        <Button size="icon" variant="ghost" onClick={() => setDelItem(it)} data-testid={`delete-item-${it.id}`} className="text-destructive hover:text-destructive">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="p-5 sm:p-8 max-w-4xl">
      <PageHeader
        title="Menu"
        subtitle={`${items.length} item(s) · drag to reorder · tap a chip to filter`}
        action={
          <Button onClick={openNew} data-testid="add-menu-item-btn" className="font-semibold">
            <Plus className="h-4 w-4 mr-1.5" /> Add item
          </Button>
        }
      />

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          data-testid="menu-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search items, category or sub-category…"
          className="pl-9"
        />
      </div>

      {/* Category filter chips */}
      {chipCats.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="menu-filter-chips">
          <button
            onClick={() => setCatFilter("")}
            data-testid="chip-all"
            className={cn(
              "text-sm rounded-full border px-3.5 py-1.5 transition-colors",
              catFilter === "" ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-accent"
            )}
          >
            All
          </button>
          {chipCats.map((c) => (
            <button
              key={c}
              onClick={() => setCatFilter(c)}
              data-testid={`chip-${c}`}
              className={cn(
                "text-sm rounded-full border px-3.5 py-1.5 transition-colors uppercase tracking-wide",
                catFilter === c ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-accent"
              )}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed border-border py-16 text-center text-muted-foreground">
          No items yet. Click “Add item” to build your menu.
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-md border border-dashed border-border py-16 text-center text-muted-foreground" data-testid="menu-no-results">
          No items match your search or filter.
        </div>
      ) : (
        <div className="space-y-7">
          {groups.map((g) => (
            <section key={g.cat} data-testid={`menu-category-${g.cat}`}>
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-base font-bold tracking-wide uppercase">{g.cat}</h2>
                <span className="text-xs text-muted-foreground">({g.count})</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <div className="space-y-5">
                {g.subs.map((s) => (
                  <div key={s.sub} data-testid={`menu-subcategory-${g.cat}-${s.sub}`}>
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{s.sub}</h3>
                    <div className="space-y-2.5">
                      {s.items.map((it) => renderCard(it, s.items))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="menu-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{form.id ? "Edit item" : "Add item"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Photo */}
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 rounded-md bg-muted grid place-items-center shrink-0 overflow-hidden">
                {form.image ? (
                  <img src={form.image} alt="preview" className="h-full w-full object-cover" data-testid="item-photo-preview" />
                ) : (
                  <ImagePlus className="h-6 w-6 text-muted-foreground" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickPhoto} data-testid="item-photo-input" />
                <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-photo-btn">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><ImagePlus className="h-4 w-4 mr-1.5" /> {form.image ? "Change photo" : "Add photo"}</>)}
                </Button>
                {form.image && (
                  <button type="button" onClick={() => setForm({ ...form, image: "" })} className="text-xs text-destructive text-left" data-testid="remove-photo-btn">
                    Remove photo
                  </button>
                )}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Item name</Label>
              <Input data-testid="item-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Chicken Biryani" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Base price (₹)</Label>
                <Input data-testid="item-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              </div>
              <div className="flex items-end gap-4 pb-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch checked={form.is_veg} onCheckedChange={(v) => setForm({ ...form, is_veg: v })} data-testid="item-veg" />
                  Veg
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch checked={form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} data-testid="item-available" />
                  In stock
                </label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Category</Label>
                {addCat ? (
                  <div className="flex gap-2">
                    <Input
                      autoFocus
                      data-testid="item-category-new"
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value.toUpperCase() })}
                      placeholder="New category"
                    />
                    <Button type="button" variant="secondary" size="icon" onClick={() => { setForm((f) => ({ ...f, category: f.category.trim() })); setAddCat(false); }} data-testid="item-category-new-done" title="Done">
                      <Check className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <Select
                    value={form.category || NONE}
                    onValueChange={(v) => {
                      if (v === ADD) { setAddCat(true); setForm({ ...form, category: "" }); }
                      else setForm({ ...form, category: v === NONE ? "" : v });
                    }}
                  >
                    <SelectTrigger data-testid="item-category"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Uncategorized</SelectItem>
                      {(form.category && !catOptions.includes(form.category) ? [...catOptions, form.category] : catOptions).map((c) => (
                        <SelectItem key={c} value={c} data-testid={`category-opt-${c}`}>{c}</SelectItem>
                      ))}
                      <SelectItem value={ADD} data-testid="category-add-new">＋ Add new…</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Sub-category</Label>
                {addSub ? (
                  <div className="flex gap-2">
                    <Input
                      autoFocus
                      data-testid="item-subcategory-new"
                      value={form.subcategory}
                      onChange={(e) => setForm({ ...form, subcategory: e.target.value.toUpperCase() })}
                      placeholder="New sub-category"
                    />
                    <Button type="button" variant="secondary" size="icon" onClick={() => { setForm((f) => ({ ...f, subcategory: f.subcategory.trim() })); setAddSub(false); }} data-testid="item-subcategory-new-done" title="Done">
                      <Check className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <Select
                    value={form.subcategory || NONE}
                    onValueChange={(v) => {
                      if (v === ADD) { setAddSub(true); setForm({ ...form, subcategory: "" }); }
                      else setForm({ ...form, subcategory: v === NONE ? "" : v });
                    }}
                  >
                    <SelectTrigger data-testid="item-subcategory"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>None</SelectItem>
                      {(form.subcategory && !subOptions.includes(form.subcategory) ? [...subOptions, form.subcategory] : subOptions).map((s) => (
                        <SelectItem key={s} value={s} data-testid={`subcategory-opt-${s}`}>{s}</SelectItem>
                      ))}
                      <SelectItem value={ADD} data-testid="subcategory-add-new">＋ Add new…</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea data-testid="item-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>

            <div className="rounded-md border border-border p-3.5">
              <div className="flex items-center justify-between mb-2">
                <Label className="mb-0">Options group (variants)</Label>
                <Button size="sm" variant="outline" onClick={addVariant} data-testid="add-variant-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Option
                </Button>
              </div>
              {form.variants.length === 0 ? (
                <p className="text-xs text-muted-foreground">No options — e.g. add Regular, Large, Extra cheese.</p>
              ) : (
                <div className="space-y-2">
                  {form.variants.map((v, i) => (
                    <div key={v._k} className="flex items-center gap-2" data-testid={`variant-row-${i}`}>
                      <Input placeholder="Option name" value={v.name} onChange={(e) => setVariant(i, "name", e.target.value)} className="flex-1" data-testid={`variant-name-${i}`} />
                      <Input placeholder="Price" type="number" value={v.price} onChange={(e) => setVariant(i, "price", e.target.value)} className="w-24" data-testid={`variant-price-${i}`} />
                      <Button size="icon" variant="ghost" onClick={() => rmVariant(i)} className="text-destructive shrink-0">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saveMut.isPending} data-testid="save-item-btn" className="font-semibold">
              {saveMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delItem} onOpenChange={(o) => !o && setDelItem(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{delItem?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>This removes the item from your menu. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => delMut.mutate(delItem.id)} className={cn("bg-destructive hover:bg-destructive/90")} data-testid="confirm-delete-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
